import { EmbedBootstrapError } from './errors';

export interface BootstrapResponse {
   embed_token: string;
   expires_at: string;
   partner_id: string;
   mode: 'test' | 'live';
   session_id: string | null;
   amount: string | null;
   currency: string | null;
   target_token: string | null;
   target_network: string | null;
   return_url: string | null;
   /**
    * Upgrade 14 — `false` when the partner is over their quota for
    * the current period. Older backends omit this field; SDK treats
    * missing as `true`.
    */
   available?: boolean;
   /** Upgrade 14 — stable code, e.g. `quota_exhausted`. */
   unavailable_reason?: string | null;
   /**
    * Upgrade 15 — kit-block flow lock from the session. SDK forwards
    * this via INIT_CONFIG so the iframe-app hides the tab strip.
    */
   flow?: 'on_ramp' | 'off_ramp' | 'swap' | null;
   /**
    * Upgrade 16 — `true` when the session was created with a
    * partner-supplied `kyc_package` (and the partner was authorised).
    * Forwarded via INIT_CONFIG so the iframe can route around its
    * own KYC step. No PII included — just the boolean.
    */
   kyc_pre_verified?: boolean;
   /** Upgrade 16 — pre-filled wallet from the session. */
   wallet_address?: string | null;
   /** Upgrade 16 — opaque partner-side identifier from the session. */
   user_reference?: string | null;
}

/**
 * Call `POST /v1/embed/bootstrap`. Exchanges (publishableKey, clientSecret?)
 * for a short-lived embed token used on subsequent iframe-API calls.
 *
 * Why this lives in the JS SDK at all when the iframe could call it
 * itself: pre-flighting from the host lets us surface fast failures
 * (bad key, origin not allowlisted) BEFORE rendering the iframe, so
 * partners get clean errors instead of a blank iframe.
 */
export async function bootstrapEmbed(input: {
   apiBaseUrl: string;
   publishableKey: string;
   clientSecret?: string;
   signal?: AbortSignal;
   fetchImpl?: typeof fetch;
}): Promise<BootstrapResponse> {
   const fetchImpl = input.fetchImpl ?? globalThis.fetch.bind(globalThis);
   if (!fetchImpl) {
      throw new EmbedBootstrapError(
         'global fetch is not available — running on a very old browser?',
      );
   }

   const body: Record<string, unknown> = {};
   if (input.clientSecret) body.clientSecret = input.clientSecret;

   let res: Response;
   try {
      res = await fetchImpl(`${input.apiBaseUrl}/v1/embed/bootstrap`, {
         method: 'POST',
         headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${input.publishableKey}`,
         },
         body: JSON.stringify(body),
         signal: input.signal,
      });
   } catch (err) {
      throw new EmbedBootstrapError(
         err instanceof Error ? err.message : `bootstrap network error: ${String(err)}`,
      );
   }

   const text = await res.text();
   const parsed = safeParse(text);

   if (!res.ok) {
      const message = extractMessage(parsed) ?? `HTTP ${res.status}`;
      throw new EmbedBootstrapError(message, res.status);
   }

   if (!parsed || typeof parsed !== 'object' || typeof (parsed as { embed_token?: unknown }).embed_token !== 'string') {
      throw new EmbedBootstrapError('bootstrap response missing embed_token');
   }
   return parsed as BootstrapResponse;
}

function safeParse(text: string): unknown {
   if (!text) return undefined;
   try {
      return JSON.parse(text);
   } catch {
      return text;
   }
}

function extractMessage(body: unknown): string | undefined {
   if (!body || typeof body !== 'object') return undefined;
   const m = (body as { message?: unknown }).message;
   if (typeof m === 'string') return m;
   if (Array.isArray(m)) return m.filter(s => typeof s === 'string').join('; ');
   return undefined;
}
